import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import { useAppSelector } from "@/store/hooks";
import { actions } from "@/store/searchSlice";
import { useGetUserQuery } from "@/store/userApi";
import { getCookie } from "cookies-next";
import { useRouter } from "next/compat/router";
import { useEffect, useMemo } from "react";
import { Configure, ConfigureProps } from "react-instantsearch";
import { useDispatch } from "react-redux";

const getCookieData = (cookies?: { [key: string]: string | undefined }) => {
  const get = (key: string) => getCookie(key) || cookies?.[key] || "";
  const purchasedBefore = get("purchasedBefore") === "true";
  const fastDelivery = get("fastDelivery") === "true";
  const freeShipping = get("freeShipping") === "true";
  const shippingRegionId = get("free_shipping_region_id");
  const authorizedVendor = get("authorizedVendor") === "true";
  return { purchasedBefore, fastDelivery, freeShipping, shippingRegionId, authorizedVendor };
};

/**
 * This component wraps around algolia's <Configure /> component in order
 * to provide defaults necessary for the application.
 *
 * By default this will always set "availability:Y"
 *
 * Besides the prop(s) listed below, all are the same as <Configure />'s props.
 * @prop {{ key: string; value: string | number }[]} filters A key/value pair object of filters you want applied
 * @returns
 */
export default function CustomConfigure({ filters, analyticsTags, url, cookies, ...rest }: CustomConfigureProps) {
  const router = useRouter();
  const cookieData = getCookieData(cookies);
  const { data: user } = useGetUserQuery();
  const purchaseBefore = useAppSelector((state) => state.searchSlice.purchasedBefore) || cookieData.purchasedBefore;
  const freeShipping = useAppSelector((state) => state.searchSlice.freeShipping) || cookieData.freeShipping;
  const fastDelivery = useAppSelector((state) => state.searchSlice.fastDelivery) || cookieData.fastDelivery;
  const shippingRegionId = useAppSelector((state) => state.searchSlice.shippingRegionId) || cookieData.shippingRegionId;
  const authorizedVendor = useAppSelector((state) => state.searchSlice.authorizedVendor) || cookieData.authorizedVendor;

  const dispatch = useDispatch();
  const { zipCode } = useLocationDetails();

  useEffect(() => {
    dispatch(actions.setShippingZipCode(zipCode));
  }, [dispatch, zipCode]);

  useEffect(() => {
    dispatch(actions.setValuesFromCookies(shippingRegionId));
  }, [user?.IsGuestUser, router?.asPath]);

  const combinedFilter = useMemo<string>(() => {
    let filterString = "";
    if (filters) {
      filters.forEach(({ key, value }) => {
        filterString += `${key}:"${value}" AND `;
      });
    }
    if (purchaseBefore && user?.UserId) {
      filterString += `(ugr_ids1:${user.UserId} OR ugr_ids2:${user.UserId}) AND `;
    }
    if (freeShipping && shippingRegionId) {
      filterString += `free_shipping_regions:${shippingRegionId} AND `;
    }
    if (fastDelivery && shippingRegionId) {
      filterString += `fast_delivery_regions: ${shippingRegionId} AND `;
    }
    if (authorizedVendor && shippingRegionId) {
      filterString += `authorized_vendor_regions: ${shippingRegionId} AND `;
    }
    filterString += "availability:Y";
    return filterString;
  }, [filters, purchaseBefore, user?.UserId, freeShipping, shippingRegionId, fastDelivery, authorizedVendor]);

  const combinedAnalyticsTags = useMemo(() => {
    let analyticsTagBuilder = [];
    if (user?.IsProfessionalOffice) {
      analyticsTagBuilder.push("isProfessionalOffice");
    }
    if (analyticsTags) {
      analyticsTagBuilder = analyticsTagBuilder.concat(analyticsTags);
    }
    if (analyticsTagBuilder.length > 0) {
      return analyticsTagBuilder;
    } else {
      return undefined;
    }
  }, [analyticsTags, user?.IsProfessionalOffice]);
  return <Configure filters={combinedFilter} analyticsTags={combinedAnalyticsTags} {...rest} />;
}

export type CustomConfigureProps = {
  filters?: { key: string; value: string | number }[];
  url?: string;
  cookies?: {
    [key: string]: string | undefined;
  };
} & Omit<ConfigureProps, "filters">;
